import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const WifiManagement = () => {
  const { token } = useParams();

  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [wifiInfo, setWifiInfo]     = useState(null);
  const [newSsid, setNewSsid]       = useState('');
  const [newPass, setNewPass]       = useState('');
  const [sameName, setSameName]     = useState(true);
  const [newSsid5, setNewSsid5]     = useState('');
  const [newPass5, setNewPass5]     = useState('');
  const [showPass, setShowPass]     = useState(false);
  const [changing, setChanging]     = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [changeDone, setChangeDone] = useState(null); // null | 'ok' | 'error'
  const [successSsid, setSuccessSsid] = useState('');

  const [devLoading, setDevLoading]     = useState(false);
  const [devWaiting, setDevWaiting]     = useState(false); // refresco automatico en curso
  const [devError, setDevError]         = useState('');
  const [devResult, setDevResult]       = useState(null);  // { count, dispositivos, onu, estado }

  const logsEndRef = useRef(null);
  const devTimerRef = useRef(null);
  const ssidTimerRef = useRef(null);
  const ssidTimer2Ref = useRef(null);

  useEffect(() => { loadData(); return () => { if (devTimerRef.current) clearTimeout(devTimerRef.current); if (ssidTimerRef.current) clearTimeout(ssidTimerRef.current); if (ssidTimer2Ref.current) clearTimeout(ssidTimer2Ref.current); }; }, [token]);
  useEffect(() => { document.title = 'Mi WIFI'; }, []);

  useEffect(() => {
    if (logsEndRef.current) logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [progressLogs]);

  /* ------------------------------------------------------------------ */
  /* Carga inicial                                                         */
  /* ------------------------------------------------------------------ */
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [infoRes, wifiRes] = await Promise.all([
        axios.get('/api/cliente/info/' + token),
        axios.get('/api/cliente/info-wifi/' + token).catch(() => ({ data: {} })),
      ]);
      if (!infoRes.data.valid) {
        setError(infoRes.data.error || 'Token invalido o expirado');
        return;
      }
      setClientInfo(infoRes.data);
      setWifiInfo(wifiRes.data);
      setNewSsid(wifiRes.data.ssid || '');
      if (!wifiRes.data.ssid) {
        // La ONU se esta despertando en segundo plano: reconsultamos a los 6s
        // y a los 15s para mostrar el SSID real cuando llegue.
        const refetchSsid = () => {
          axios.get('/api/cliente/info-wifi/' + token).then(r2 => {
            if (r2.data && r2.data.ssid) {
              setWifiInfo(r2.data);
              setNewSsid(prev => prev || r2.data.ssid);
            }
          }).catch(() => {});
        };
        if (ssidTimerRef.current) clearTimeout(ssidTimerRef.current);
        if (ssidTimer2Ref.current) clearTimeout(ssidTimer2Ref.current);
        ssidTimerRef.current = setTimeout(refetchSsid, 6000);
        ssidTimer2Ref.current = setTimeout(refetchSsid, 15000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar la informacion');
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Dispositivos conectados: respuesta instantanea + refresco automatico  */
  /* ------------------------------------------------------------------ */
  const fetchDispositivos = async (auto) => {
    setDevLoading(true);
    setDevError('');
    try {
      const res = await axios.get('/api/cliente/dispositivos/' + token);
      const d = res.data || {};
      setDevResult({ count: d.count, dispositivos: d.dispositivos || [], onu: d.onu, estado: d.estado });
      const pendia = d.count === null || d.count === undefined;
      if ((pendia || d.refreshing) && auto) {
        // El despertar de la ONU va en segundo plano: reconsultamos una vez
        // para mostrar el dato fresco.
        setDevWaiting(true);
        if (devTimerRef.current) clearTimeout(devTimerRef.current);
        devTimerRef.current = setTimeout(async () => {
          try {
            const r2 = await axios.get('/api/cliente/dispositivos/' + token);
            const d2 = r2.data || {};
            setDevResult({ count: d2.count, dispositivos: d2.dispositivos || [], onu: d2.onu, estado: d2.estado });
          } catch (_) {}
          setDevWaiting(false);
          setDevLoading(false);
        }, 8000);
        return;
      }
      setDevWaiting(false);
      setDevLoading(false);
    } catch (err) {
      setDevError(err.response?.data?.error || 'No se pudo consultar la ONU');
      setDevWaiting(false);
      setDevLoading(false);
    }
  };

  const handleVerDispositivos = () => {
    setDevResult(null);
    fetchDispositivos(true);
  };

  /* ------------------------------------------------------------------ */
  /* Cambiar WiFi con progreso SSE en tiempo real                        */
  /* ------------------------------------------------------------------ */
  const handleChange = async () => {
    const ssidChanged = newSsid.trim() && newSsid.trim() !== (wifiInfo?.ssid || '');
    const passChanged = newPass.trim().length > 0;
    if (!ssidChanged && !passChanged) {
      alert('Ingresa una nueva contrasena o un nuevo nombre de red.');
      return;
    }
    if (ssidChanged && !passChanged) {
      alert('Para cambiar el nombre de red debes ingresar tambien la contrasena WiFi (requerido por OLTCloud).');
      return;
    }
    if (passChanged && newPass.trim().length < 8) {
      alert('La contrasena debe tener al menos 8 caracteres.');
      return;
    }
    if (passChanged && newPass.trim().length > 63) {
      alert('La contrasena no puede exceder 63 caracteres.');
      return;
    }
    if (ssidChanged && newSsid.trim().length > 32) {
      alert('El nombre de red no puede exceder 32 caracteres.');
      return;
    }
    const seguro = v => /^[A-Za-z0-9 .\-_]+$/.test(v);
    if (ssidChanged && !seguro(newSsid.trim())) {
      alert('Tu equipo no acepta simbolos especiales en el nombre de red. Usa solo letras, numeros, espacios, guiones o puntos.');
      return;
    }
    if (!sameName && newSsid5.trim() && !seguro(newSsid5.trim())) {
      alert('Tu equipo no acepta simbolos especiales en el nombre de red 5GHz. Usa solo letras, numeros, espacios, guiones o puntos.');
      return;
    }
    if (!sameName) {
      if ((newSsid5.trim() && newSsid5.trim().length > 32)) {
        alert('El nombre de red 5GHz no puede exceder 32 caracteres.');
        return;
      }
      if (newPass5.trim() && (newPass5.trim().length < 8)) {
        alert('La contrasena 5GHz debe tener al menos 8 caracteres.');
        return;
      }
    }

    setChanging(true);
    setChangeDone(null);
    setProgressLogs([{ step: 0, msg: 'Enviando cambio a OLTCloud (API)...', status: 'loading' }]);

    try {
      const res = await axios.post('/api/cliente/wifi', {
        token,
        ssid: ssidChanged ? newSsid.trim() : '',
        wifipass: passChanged ? newPass.trim() : '',
        sameName,
        ssid5: sameName ? '' : (newSsid5.trim() || ''),
        wifipass5: sameName ? '' : (newPass5.trim() || ''),
      });

      if (!res.data.success) {
        setProgressLogs(p => [...p, { step: 0, msg: 'Error: ' + (res.data.error || 'Error desconocido'), status: 'error' }]);
        setChangeDone('error');
        setChanging(false);
        return;
      }

      const processId = res.data.processId;
      const es = new EventSource('/api/cliente/progreso/' + processId);

      es.addEventListener('progress', (e) => {
        try {
          const data = JSON.parse(e.data);
          setProgressLogs(p => [...p, { step: data.paso, msg: data.msg, status: data.estado }]);
        } catch (_) {}
      });

      es.addEventListener('done', (e) => {
        es.close();
        try {
          const data = JSON.parse(e.data);
          if (data.success) {
            setProgressLogs(p => [...p, { step: 99, msg: 'WiFi actualizado correctamente via OLTCloud', status: 'success' }]);
            setSuccessSsid(newSsid.trim() || wifiInfo?.ssid || '');
            setChangeDone('ok');
            setWifiInfo(prev => ({ ...prev, ssid: newSsid.trim() || prev?.ssid, password: passChanged ? newPass.trim() : prev?.password }));
            setNewPass('');
            setNewPass5('');
          } else {
            setProgressLogs(p => [...p, { step: 99, msg: (data.error || 'Error al aplicar cambios'), status: 'error' }]);
            setChangeDone('error');
          }
        } catch (_) { setChangeDone('error'); }
        setChanging(false);
      });

      es.onerror = () => {
        es.close();
        setProgressLogs(p => [...p, { step: 99, msg: 'Se perdio la conexion con OLTCloud', status: 'error' }]);
        setChangeDone('error');
        setChanging(false);
      };
    } catch (err) {
      setProgressLogs(p => [...p, { step: 0, msg: 'Error: ' + (err.response?.data?.error || err.message), status: 'error' }]);
      setChangeDone('error');
      setChanging(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Helpers visuales                                                     */
  /* ------------------------------------------------------------------ */
  const statusColor = (s) => {
    if (s === 'success') return '#22c55e';
    if (s === 'error')   return '#ef4444';
    if (s === 'warning') return '#f59e0b';
    return '#60a5fa';
  };

  const statusIcon = (s) => {
    if (s === 'success') return '\u2705';
    if (s === 'error')   return '\u274c';
    if (s === 'warning') return '\u26a0\ufe0f';
    return '\u23f3';
  };

  /* ------------------------------------------------------------------ */
  /* Estados: cargando / error                                            */
  /* ------------------------------------------------------------------ */
  if (loading) {
    return (
      <div style={styles.root}>
        <div style={styles.card}>
          <div style={styles.logoRow}>
            <div style={styles.logoDot} />
            <span style={styles.logoText}>MI WIFI Portal</span>
          </div>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={styles.spinner} />
            <p style={{ color: '#94a3b8', marginTop: 16 }}>Verificando token...</p>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.root}>
        <div style={styles.card}>
          <div style={styles.logoRow}>
            <div style={styles.logoDot} />
            <span style={styles.logoText}>MI WIFI Portal</span>
          </div>
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F6AB;</div>
            <h2 style={{ color: '#ef4444', marginBottom: 8 }}>Acceso denegado</h2>
            <p style={{ color: '#94a3b8' }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Portal principal                                                     */
  /* ------------------------------------------------------------------ */
  return (
    <div style={styles.root}>
      <div style={styles.card}>

        {/* Encabezado OLTCloud */}
        <div style={{ marginBottom: 20 }}>
          <div style={styles.logoRow}>
            <div style={styles.logoDot} />
            <span style={styles.logoText}>MI WIFI Portal</span>
          </div>
          <div style={styles.clientBadge}>
            <span style={styles.clientName}>{clientInfo?.clientName || 'Cliente'}</span>
            <span style={styles.clientId}>PPPoE: {clientInfo?.pppoePartial || '***'}</span>
          </div>
        </div>

        {/* WiFi actual: 2.4GHz / 5GHz reales + contrasena si esta guardada */}
        <div style={styles.infoBox}>
          {wifiInfo?.ssid5 && wifiInfo.ssid5 !== wifiInfo?.ssid ? (
            <div>
              <div>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>Red WiFi actual (2.4GHz):</span>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginLeft: 8 }}>
                  {wifiInfo?.ssid || '\u2014'}
                </span>
              </div>
              <div style={{ marginTop: 4 }}>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>Red WiFi actual (5GHz):</span>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 16, marginLeft: 8 }}>
                  {wifiInfo.ssid5}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>Red WiFi actual:</span>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 18, marginLeft: 10 }}>
                  {wifiInfo?.ssid || '\u2014'}
                </span>
              </div>
            </div>
          )}
          {wifiInfo?.password ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
              <div>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>Contrasena actual:</span>
                <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 15, marginLeft: 10, fontFamily: 'monospace' }}>
                  {showPass ? wifiInfo.password : '\u2022'.repeat(Math.min(wifiInfo.password.length, 12))}
                </span>
              </div>
              <button
                style={styles.eyeBtn}
                onClick={() => setShowPass(p => !p)}
                title={showPass ? 'Ocultar contrasena' : 'Mostrar contrasena'}
              >
                {showPass ? '\u{1F441}' : '\u2022\u2022\u2022'}
              </button>
            </div>
          ) : null}
        </div>

        {/* Formulario de cambio */}
        {!changing && changeDone !== 'ok' && (
          <div style={styles.form}>
            <label style={styles.label}>Nuevo nombre de red (SSID)</label>
            <input
              style={styles.input}
              type="text"
              value={newSsid}
              maxLength={32}
              placeholder={wifiInfo?.ssid || 'Nombre actual'}
              onChange={e => setNewSsid(e.target.value)}
            />
            <span style={styles.hint}>{newSsid.length}/32 caracteres</span>

            <label style={styles.label}>Nueva contrasena WiFi</label>
            <input
              style={styles.input}
              type="text"
              value={newPass}
              maxLength={63}
              placeholder="Minimo 8 caracteres"
              onChange={e => setNewPass(e.target.value.replace(/\s/g, ''))}
            />
            <span style={styles.hint}>{newPass.length}/63 sin espacios</span>

            {/* 2.4GHz / 5GHz */}
            <label style={styles.checkRow}>
              <input
                type="checkbox"
                checked={sameName}
                onChange={e => {
                  setSameName(e.target.checked);
                  if (!e.target.checked && !newSsid5) setNewSsid5((newSsid.trim() || wifiInfo?.ssid || '') + '5G');
                }}
              />
              <span style={{ color: '#cbd5e1', fontSize: 13 }}>
                Usar el mismo nombre y contrasena para 2.4GHz y 5GHz
              </span>
            </label>

            {!sameName && (
              <div style={{ border: '1px solid rgba(96,165,250,0.25)', borderRadius: 10, padding: 12, marginTop: 4 }}>
                <div style={{ fontSize: 12, color: '#60a5fa', fontWeight: 700, marginBottom: 8 }}>Red 5GHz separada</div>
                <label style={styles.label}>Nombre de red 5GHz</label>
                <input
                  style={styles.input}
                  type="text"
                  value={newSsid5}
                  maxLength={32}
                  placeholder={(newSsid.trim() || wifiInfo?.ssid || '') + '5G'}
                  onChange={e => setNewSsid5(e.target.value)}
                />
                <label style={styles.label}>Contrasena 5GHz</label>
                <input
                  style={styles.input}
                  type="text"
                  value={newPass5}
                  maxLength={63}
                  placeholder="Minimo 8 caracteres (vacio = igual a 2.4GHz)"
                  onChange={e => setNewPass5(e.target.value.replace(/\s/g, ''))}
                />
              </div>
            )}

            <button
              style={{
                ...styles.btn,
                opacity: (!newPass.trim() && newSsid.trim() === (wifiInfo?.ssid || '')) ? 0.4 : 1,
              }}
              disabled={!newPass.trim()}
              onClick={handleChange}
            >
              Aplicar cambios
            </button>
          </div>
        )}

        {/* Dispositivos conectados */}
        <div style={{ marginTop: 16 }}>
          <button
            style={{ ...styles.btn, background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd', fontSize: 13, padding: '10px 14px' }}
            disabled={devLoading}
            onClick={handleVerDispositivos}
          >
            {devLoading ? 'Consultando la ONU...' : '\u{1F4F1} Ver dispositivos conectados'}
          </button>

          {devError && (
            <p style={{ color: '#f87171', fontSize: 13, marginTop: 8 }}>{devError}</p>
          )}

          {devResult && (
            <div style={{ marginTop: 10, border: '1px solid rgba(148,163,184,0.2)', borderRadius: 10, padding: 12 }}>
              {devResult.count === null || devResult.count === undefined ? (
                <span style={{ color: '#94a3b8', fontSize: 13 }}>
                  {devWaiting ? 'Despertando la ONU y leyendo dispositivos...' : 'Presiona de nuevo en unos segundos para ver el resultado.'}
                </span>
              ) : (
                <div>
                  <div style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 700, marginBottom: 6 }}>
                    {devResult.count} dispositivo{devResult.count === 1 ? '' : 's'} conectado{devResult.count === 1 ? '' : 's'}
                    {devWaiting && <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 12 }}> (actualizando...)</span>}
                  </div>
                  {devResult.dispositivos.length === 0 ? (
                    <span style={{ color: '#94a3b8', fontSize: 13 }}>
                      La ONU no reporta equipos conectados en este momento.
                    </span>
                  ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {devResult.dispositivos.map((d, i) => (
                        <li key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13, color: '#cbd5e1', padding: '4px 0', borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
                          <span style={{ color: '#fff', fontWeight: 600 }}>{d.nombre}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#94a3b8' }}>
                            {[d.ip, d.mac].filter(Boolean).join(' \u00b7 ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Progreso SSE en tiempo real */}
        {(changing || progressLogs.length > 0) && changeDone !== 'ok' && (
          <div style={styles.progressBox}>
            <div style={styles.progressTitle}>
              <span style={{ color: '#60a5fa' }}>Progreso en OLTCloud</span>
              {changing && <div style={styles.pulse} />}
            </div>
            <div style={styles.logList}>
              {progressLogs.map((log, i) => (
                <div
                  key={i}
                  style={{ display: 'flex', alignItems: 'flex-start', fontSize: 13, lineHeight: 1.5, gap: 8, color: statusColor(log.status) }}
                >
                  <span style={{ minWidth: 22 }}>{statusIcon(log.status)}</span>
                  <span>{log.msg}</span>
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          </div>
        )}

        {/* Exito */}
        {changeDone === 'ok' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>&#x2705;</div>
            <h2 style={{ color: '#22c55e', margin: '0 0 8px' }}>Cambios aplicados</h2>
            <p style={{ color: '#94a3b8', marginBottom: 20 }}>
              Tu red WiFi fue actualizada. Espera unos minutos y reconecta con la nueva clave.
            </p>
            {successSsid && (
              <div style={{ display: 'inline-block', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, padding: '8px 16px', color: '#86efac', fontSize: 14 }}>
                Red: <strong>{successSsid}</strong>
              </div>
            )}
            <button
              style={{ ...styles.btn, marginTop: 24 }}
              onClick={() => { setChangeDone(null); setProgressLogs([]); loadData(); }}
            >
              Hacer otro cambio
            </button>
          </div>
        )}

        {/* Error con reintento */}
        {changeDone === 'error' && (
          <button
            style={{ ...styles.btn, background: '#7c3aed', marginTop: 8 }}
            onClick={() => { setChangeDone(null); setProgressLogs([]); }}
          >
            Intentar de nuevo
          </button>
        )}

        <div style={{ marginTop: 24, textAlign: 'center', color: '#334155', fontSize: 11, letterSpacing: 0.5 }}>
          Fibernet RD &middot; Powered by OLTCloud
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse-dot {
          0%, 100% { opacity: 0.4; transform: scale(0.8); }
          50%       { opacity: 1;   transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Objeto de estilos                                                    */
/* ------------------------------------------------------------------ */
const styles = {
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: 420,
    background: 'rgba(15, 23, 42, 0.85)',
    border: '1px solid rgba(148, 163, 184, 0.15)',
    borderRadius: 20,
    padding: '28px 24px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 6,
  },
  logoDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    background: '#7c3aed',
    boxShadow: '0 0 14px rgba(124,58,237,0.9)',
  },
  logoText: {
    color: '#a78bfa',
    fontWeight: 800,
    fontSize: 18,
    letterSpacing: 1.5,
  },
  clientBadge: {
    marginTop: 10,
    background: 'rgba(30, 41, 59, 0.8)',
    border: '1px solid rgba(148, 163, 184, 0.15)',
    borderRadius: 12,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  clientName: {
    color: '#fff',
    fontWeight: 800,
    fontSize: 17,
  },
  clientId: {
    color: '#64748b',
    fontSize: 12,
    fontFamily: 'monospace',
  },
  infoBox: {
    marginTop: 14,
    background: 'rgba(16, 185, 129, 0.08)',
    border: '1px solid rgba(16, 185, 129, 0.25)',
    borderRadius: 12,
    padding: '12px 14px',
  },
  form: {
    marginTop: 18,
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    color: '#94a3b8',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.8,
    marginTop: 12,
    marginBottom: 6,
    display: 'block',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    background: 'rgba(15, 23, 42, 0.7)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    borderRadius: 10,
    padding: '12px 14px',
    color: '#fff',
    fontSize: 15,
    outline: 'none',
  },
  hint: {
    color: '#475569',
    fontSize: 11,
    marginTop: 4,
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
    cursor: 'pointer',
  },
  eyeBtn: {
    background: 'rgba(59,130,246,0.12)',
    border: '1px solid rgba(59,130,246,0.35)',
    borderRadius: 8,
    color: '#93c5fd',
    fontSize: 12,
    padding: '4px 10px',
    cursor: 'pointer',
  },
  btn: {
    marginTop: 18,
    width: '100%',
    background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
    border: 'none',
    borderRadius: 12,
    padding: '14px 16px',
    color: '#fff',
    fontWeight: 800,
    fontSize: 15,
    cursor: 'pointer',
  },
  progressBox: {
    marginTop: 16,
    background: 'rgba(15, 23, 42, 0.7)',
    border: '1px solid rgba(148, 163, 184, 0.15)',
    borderRadius: 12,
    padding: 14,
  },
  progressTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 8,
  },
  pulse: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: '#60a5fa',
    animation: 'pulse-dot 1.2s infinite',
  },
  logList: {
    maxHeight: 200,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  spinner: {
    width: 34,
    height: 34,
    margin: '0 auto',
    border: '3px solid rgba(148,163,184,0.25)',
    borderTopColor: '#7c3aed',
    borderRadius: '50%',
    animation: 'spin 0.9s linear infinite',
  },
};

export default WifiManagement;
